import { Box, Button, styled, Typography, useTheme } from "@mui/material"
import AspectRatio from "components/common/AspectRatio"
import useHostEnv from "hooks/useHostEnv"
import useLaunchpadContext from "hooks/useLaunchpadContext"
import getConventionAddress from "lib/conventions/getConventionAddress"
import createGoogleMapLink from "lib/google-maps/createGoogleMapLink"
import "mapbox-gl/dist/mapbox-gl.css"
import React from "react"
import MapboxGL, { Marker, NavigationControl } from "react-map-gl"

const LaunchpadLocation = () => {
  const { convention } = useLaunchpadContext()
  const theme = useTheme()
  const hostEnv = useHostEnv()

  const conventionAddress = getConventionAddress(convention)
  const conventionGoogleMapLink =
    convention.latitude &&
    convention.longitude &&
    createGoogleMapLink({
      longitude: convention.longitude,
      latitude: convention.latitude,
    })

  const shouldHideLocation =
    !convention.location_name &&
    !convention.location_instructions &&
    !conventionGoogleMapLink &&
    !conventionAddress

  // there is no point of showing location section since no data is present
  if (shouldHideLocation) return null

  return (
    <_Container component="section">
      <Typography variant="header2" component="h2" marginBottom="20px">
        Location
      </Typography>
      {convention.location_name && (
        <Typography variant="header5" component="p" marginTop="10px">
          {convention.location_name}
        </Typography>
      )}
      {conventionAddress && (
        <Typography variant="paragraphRegular" marginTop="10px" component="p">
          {conventionAddress}
        </Typography>
      )}
      {convention.location_instructions && (
        <Typography variant="paragraphRegular" marginTop="10px" component="p">
          {convention.location_instructions}
        </Typography>
      )}
      {conventionGoogleMapLink && (
        <a href={conventionGoogleMapLink} rel="noopener noreferrer" target="_blank">
          <OpenGoogleMapButton>
            OPEN IN GOOGLE MAPS
          </OpenGoogleMapButton>
        </a>
      )}

      {hostEnv?.NEXT_PUBLIC_MAPBOX_API_KEY && convention.longitude && convention.latitude && (
        <_MapContainer ratio={535 / 281}>
          <MapboxGL
            mapboxAccessToken={hostEnv?.NEXT_PUBLIC_MAPBOX_API_KEY}
            mapStyle="mapbox://styles/mapbox/streets-v11"
            style={{ borderRadius: "10px" }}
            initialViewState={{
              longitude: convention.longitude,
              latitude: convention.latitude,
              zoom: 12,
            }}>
            <Marker
              longitude={convention.longitude}
              latitude={convention.latitude}
              color={theme.palette.primary.main}
            />
            <NavigationControl />
          </MapboxGL>
        </_MapContainer>
      )}
    </_Container>
  )
}

export default LaunchpadLocation

const _Container = styled(Box)(({}) => ({
  maxWidth: "842px",
})) as typeof Box

const OpenGoogleMapButton = styled(Button)(({ theme }) => ({
  display: "inline-block",
  marginTop: "10px",
  ...theme.typography.paragraphBold,
}))

const _MapContainer = styled(AspectRatio)({
  marginTop: "20px",
  display: "flex",
  justifyContent: "stretch",
  alignItems: "stretch",
  maxWidth: "535px",
  width: "100%",
  borderRadius: "10px",
}) as typeof AspectRatio
